import React, { useState } from 'react';
import PaperIcon from '../icons/Paper';
import SettingsIcon from '../icons/Settings';
import UsersIcon from '../icons/Users';
import EmailIcon from '../icons/Email';
import { connect } from 'react-redux';
import { can } from '../helpers/permissions';
import authSlice from '../store/auth/slice';
import { Conference, ConferenceState, Role, RoleType, Track } from '../store/conference/types';
import {
  selectCurrentConference,
  selectCurrentRole,
  selectCurrentTrack,
  selectExistRolesHandleReviewers,
  selectFormState,
  selectImpersonatedTrackId,
  selectIsRequiredInfoCompleted,
  selectSideMenuState,
  selectTrackUsingKeywords,
  selectUser,
} from '../store/selectors';
import sidemenuSlice from '../store/sidemenu/slice';
import { AppState } from '../store/state';
import { AppDispatch } from '../store/store';
import { appRoutes, getRouteByName, getRouteByPath, isWizardPage } from './routes';
import { generatePath } from 'react-router';
import history from '../store/history';
import MainLayout from '../layouts/MainLayout';
import { Link, SelectRadioButton, ShareLinkButton } from '../components/ui';
import { UserState } from '../store/user/types';
import conferenceSlice from '../store/conference/slice';
import { MainItems, MenuItems } from '../layouts/types';
import { useTheme } from '@material-ui/core/styles';
import Bidding from '../icons/Bidding';
import Assignment from '../icons/Assignment';
import styled, { ThemeProvider } from 'styled-components';
import ConversationIcon from '../icons/Conversation';
import Dashboard from '../icons/Dashboard';
import { ReactComponent as LogoMini } from '../images/logo-mini.svg';
import { ReactComponent as Logo } from '../images/logo.svg';
import ChevronDownIcon from '../icons/ChevronDown';
import { Link as RouterLink } from 'react-router-dom';
import { doesHandleReviewers, getUserRoles } from '../helpers/role';
import { getSupervisedRoleIds, getSupervisorRoleIds } from '../helpers/form';
import { FormState } from '../store/form/types';
// import LanguageSelector from '../components/ui/inputs/LanguageSelector/LanguageSelector';
import { useTranslation } from 'react-i18next';
import { getRoleName } from '../helpers/translations';
import { DEFAULT_PRIMARY_BACKGROUND_COLOR } from '../theme';
import Menu from '../icons/Menu';
import { useIsMobile } from '../helpers/window';

interface LayoutCtrlProps {
  menuOpen: boolean;
  setMenuOpen: (menuOpen: boolean) => void;
  menuclick: (item: string) => void;
  children: React.ReactNode;
  currentTrack: Track | null;
  currentRole: Role | null;
  currentConference: Conference | null;
  user: UserState;
  conferenceState: ConferenceState;
  selectRoleAction: (trackId: number, roleId: number) => void;
  isImpersonated: boolean;
  leaveImpersonationAction: () => void;
  trackUsingKeywords: boolean | null;
  existRolesHandleReviewers: boolean;
  isRequiredInfoCompleted: boolean;
  formStoreState: FormState;
  backgroundColor?: string;
}

const mainItems: { [key in RoleType]: MainItems } = {
  author: [
    {
      name: 'Submissions',
      route_name: 'RouteConferenceHome',
      icon: PaperIcon,
    },
    {
      name: 'User keywords',
      route_name: 'RouteKeywords',
      icon: SettingsIcon,
    },
    {
      name: 'Communications',
      route_name: 'RouteRecruitmentPage',
      icon: EmailIcon,
      subitems: [
        {
          name: 'Email history',
          route_name: 'RouteEmailHistoryPage',
        },
      ],
    },
  ],
  reviewer: [
    {
      name: 'Dashboard',
      route_name: 'RouteDashboard',
      icon: Dashboard,
      params: { tab: 'submissions' },
    },
    {
      name: 'People',
      route_name: 'RoutePeople',
      icon: UsersIcon,
    },
    {
      name: 'Submissions',
      route_name: 'RouteConferenceHome',
      icon: PaperIcon,
    },
    {
      name: 'Bidding',
      route_name: 'RouteBidding',
      icon: Bidding,
    },
    {
      name: 'User keywords',
      route_name: 'RouteKeywords',
      icon: SettingsIcon,
    },
    {
      name: 'Communications',
      route_name: 'RouteRecruitmentPage',
      icon: EmailIcon,
      subitems: [
        {
          name: 'Bulk email reviewers',
          route_name: 'RouteBulkEmailReviewersPage',
        },
        {
          name: 'Bulk email supervisors',
          route_name: 'RouteBulkEmailSupervisorsPage',
        },
        {
          name: 'Email history',
          route_name: 'RouteEmailHistoryPage',
        },
      ],
    },
  ],
  chair: [
    {
      name: 'Dashboard',
      route_name: 'RouteDashboard',
      icon: Dashboard,
      params: { tab: 'submissions' },
    },
    {
      name: 'Recruitment',
      route_name: 'RouteRecruitmentPage',
      icon: EmailIcon,
    },
    {
      name: 'People',
      route_name: 'RoutePeople',
      icon: UsersIcon,
    },
    {
      name: 'Submissions',
      route_name: 'RouteAllSubmissions',
      icon: PaperIcon,
    },
    {
      name: 'Bidding',
      route_name: 'RouteAllReviewersBids',
      icon: Bidding,
      subitems: [
        {
          name: 'Reviewers bids',
          route_name: 'RouteAllReviewersBids',
          icon: Bidding,
        },
        {
          name: 'Submissions bids',
          route_name: 'RouteAllSubmissionsBids',
          icon: Bidding,
        },
      ],
    },
    {
      name: 'Assignments',
      route_name: 'RouteAssignmentBulkImport',
      icon: Assignment,
      subitems: [
        {
          name: 'Bulk import',
          route_name: 'RouteAssignmentBulkImport',
        },
        {
          name: 'Reviewers to papers',
          route_name: 'RouteAssignmentReviewersToPapers',
        },
        {
          name: 'Papers to reviewers',
          route_name: 'RouteAssignmentPapersToReviewers',
        },
        {
          name: 'Reviewers to reviewers',
          route_name: 'RouteAssignmentsReviewersToReviewers',
        },
      ],
    },
    {
      name: 'Communications',
      route_name: 'RouteRecruitmentPage',
      icon: EmailIcon,
      subitems: [
        {
          name: 'Email templates',
          route_name: 'RouteEmailTemplatesPage',
        },
        {
          name: 'Email history',
          route_name: 'RouteEmailHistoryPage',
        },
      ],
    },
    {
      name: 'Chair settings',
      icon: SettingsIcon,
      route_name: 'RouteSettingsRoles',
      subitems: [
        {
          name: 'Roles',
          route_name: 'RouteSettingsRoles',
        },
        {
          name: 'Keywords',
          route_name: 'RouteSettingsKeywords',
        },

        {
          name: 'Paper status',
          route_name: 'RouteSettingsPaperStatus',
        },

        {
          name: 'Forms',
          route_name: 'RouteSettingsForms',
        },
        {
          name: 'Planning',
          route_name: 'RouteSettingsPlanning',
        },
        {
          name: 'Announcements',
          route_name: 'RouteSettingsAnnouncements',
        },
        {
          name: 'Data & API access',
          route_name: 'RouteDataAPIAccess',
        },
      ],
    },
  ],
};

const LayoutCtrl: React.FC<LayoutCtrlProps> = ({
  menuOpen,
  setMenuOpen,
  menuclick,
  currentTrack,
  currentRole,
  currentConference,
  user,
  conferenceState,
  selectRoleAction,
  children,
  isImpersonated,
  leaveImpersonationAction,
  trackUsingKeywords,
  existRolesHandleReviewers,
  isRequiredInfoCompleted,
  formStoreState,
  backgroundColor = DEFAULT_PRIMARY_BACKGROUND_COLOR,
}) => {
  const { t } = useTranslation();
  const [roleValue, setRole] = React.useState<string | undefined>(currentRole?.id.toString());
  const [profileMenu, setProfileMenu] = useState(false);

  const route = getRouteByPath(window.location.pathname);

  const selectedOption = route.main_menu_option || '';
  const theme = useTheme();

  const isMobile = useIsMobile();

  const [menuMobileOpen, setMenuMobileOpen] = useState(false);

  const getMainItems = (conference: Conference, track: Track, currentRole: Role | null): MainItems => {
    let items: MainItems = [];
    if (currentRole?.type && mainItems[currentRole.type]) {
      items = [...mainItems[currentRole.type]];
    }

    if (process.env.REACT_APP_ENV != 'production' && currentRole?.type === 'reviewer') {
      items.push({
        name: 'Debug',
        route_name: 'RouteDebugPage',
        icon: SettingsIcon,
      });
    }

    const params = { conference: conference.slug, track: track.slug };
    return items.map((item) => {
      return {
        ...item,
        params: { ...item.params, ...params },
        subitems: item.subitems ? item.subitems.map((subItem) => ({ ...subItem, params })) : undefined,
      };
    });
  };

  const itemClicked = (item: string) => {
    menuclick(item);
    let menuItem = undefined;
    for (let i = 0; i < menuItems.length; i++) {
      if (menuItems[i].name === item) {
        menuItem = menuItems[i];
        break;
      }
      if (menuItems[i].subitems) {
        const num_subitems = menuItems[i].subitems?.length ?? 0;
        for (let j = 0; j < num_subitems; j++) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          if (menuItems[i].subitems[j].name === item) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            menuItem = menuItems[i].subitems[j];
          }
        }
      }
    }
    const route_name = menuItem?.route_name || '';
    const findroute = appRoutes.filter((element) => element.name === route_name);
    if (findroute.length > 0) {
      const path = menuItem?.params ? generatePath(findroute[0].path, menuItem.params) : findroute[0].path;
      history.push(path);
    }
  };

  const doClick = (item: MenuItems, isMenuSubitem: boolean) => {
    if (item.name === 'Hide menu') {
      setMenuOpen(!menuOpen);
    } else if (isMenuSubitem) {
      itemClicked(item.name);
    } else if (item.subitems) {
      setMenuOpen(false);
    } else {
      itemClicked(item.name);
    }
  };

  const handleRoleSelectChange = (role: string) => {
    if (currentTrack) {
      selectRoleAction(currentTrack.id, parseInt(role));
    }
  };

  const handleAvatarClick = () => {
    history.push(getRouteByName('RouteProfile').path);
  };

  const handleOnMenuToggle = () => setMenuMobileOpen((prev) => !prev);

  let menuItems: MainItems = [];
  let header;
  const userName = user.data.person.full_name;
  const isTrackIn = !!(currentConference && currentTrack && currentRole);
  const allowMobile = route.allow_mobile === true; // To know if route is allowed in mobile version

  // Build selectors options
  const roleOptions: {
    id: string;
    label: string;
  }[] = [];

  let logo = (
    <div>
      {!isMobile && <StyledBeta className="betaLabel">BETA</StyledBeta>}
      {!menuOpen ? <Logo title="Home" style={{ width: '13.2rem' }} /> : <LogoMini title="Home" />}
    </div>
  );
  {
    /* Logo is clickable only if the user is not impersonated */
  }
  if (!isImpersonated) {
    logo = <RouterLink to={getRouteByName('RouteConferences').path}>{logo}</RouterLink>;
  }

  //let url = new URL(window.location.origin + getRouteByName('RouteLogout').path);
  //url = addSearchParams(url, { next: window.location.pathname });

  const logoutEl = (
    <RouterLink to={getRouteByName('RouteLogout').path}>
      <span className="cursor-pointer flex" style={{ flexGrow: 1, color: theme.palette.primary.main }}>
        {t('Log out')}
      </span>
    </RouterLink>
  );

  const menuButton = (
    <button className={`mobileCtrlMenuButtonWrapper${!isTrackIn ? ' noTrack' : ''}`} onClick={handleOnMenuToggle}>
      <Menu />
    </button>
  );

  const logoWrapper = (
    <div className="flex items-center">
      {isMobile && menuButton}

      <div className={`relative logoWrapperConferences`} style={{ marginLeft: '-32px' }}>
        {logo}
      </div>
    </div>
  );

  const profileMenuBlock = (
    <div className="flex justify-between items-center">
      {/* LANGUAGE SELECTOR */}
      {/*<LanguageSelector className="mr-4" changeLanguageOnChange={true} />*/}

      {/* PROFILE BUTTON */}
      <div className="flex flex-col relative">
        <div
          className={`flex justify-center my-3 self-center cursor-pointer mobileCtrlMenuProfileWrapper`}
          onClick={() => setProfileMenu(!profileMenu)}
          style={{ color: theme.palette.primary.main }}
        >
          {user.data.person.avatar_url ? (
            <StyledMenuProfile className="menuProfile">
              <img src={user.data.person.avatar_url} alt="profile" />
            </StyledMenuProfile>
          ) : (
            <UsersIcon width="3rem" height="3rem" />
          )}

          {!isMobile && (
            <>
              <div className="self-center">
                <p className="font-medium text-base ml-2 mr-2">{userName}</p>
                {isTrackIn && (
                  <p className="text-xs text-gray-500 ml-2">
                    {t('Role')}: {currentRole ? getRoleName(currentRole, t) : ''}
                  </p>
                )}
              </div>
              <div className="self-center ml-2">
                <ChevronDownIcon />
              </div>
            </>
          )}
        </div>
        {/* PROFILE MENU */}
        {profileMenu && (
          <StyledProfileMenu className="border-t border-gray-200 rounded-b-md shadow-md profileMenu">
            {isTrackIn && (
              <div className="flex flex-wrap">
                <span style={{ flexGrow: 1 }}>{t('Your role')}</span>

                <SelectRadioButton
                  value={roleValue}
                  options={roleOptions ?? roleOptions}
                  onChange={roleOptions ? handleRoleSelectChange : undefined}
                  className="w-full flex flex-col ml-3 mt-1"
                />
              </div>
            )}

            {!isImpersonated && !isWizardPage(route.name) && (
              <>
                {isTrackIn && <hr className="divide-y divide-gray-950 mt-2 mb-6"></hr>}

                <div className="flex mb-4">
                  <Link className="cursor-pointer flex" onClick={handleAvatarClick}>
                    <span style={{ flexGrow: 1 }}>{t('Profile')}</span>
                  </Link>
                </div>
              </>
            )}

            <hr className="divide-y divide-gray-950 mt-2 mb-4"></hr>

            {!isImpersonated && isTrackIn && (
              <div className="flex my-2">
                <RouterLink to={getRouteByName('RouteConferences').path}>
                  <div className="flex items-center 'cursor-pointer">
                    <span style={{ flexGrow: 1, color: theme.palette.primary.main }}>{t('Exit conference')}</span>
                  </div>
                </RouterLink>
              </div>
            )}
            <div className="flex my-2">{logoutEl}</div>
          </StyledProfileMenu>
        )}
      </div>
    </div>
  );

  const HeaderBarWrapper: React.FC = ({ children }) => (
    <div className="flex justify-between w-full">
      {children}
      {profileMenuBlock}
    </div>
  );

  if (
    !currentConference ||
    !currentTrack ||
    !currentTrack.wizard_completed ||
    (!isRequiredInfoCompleted && !isImpersonated)
  ) {
    header = <HeaderBarWrapper>{logoWrapper}</HeaderBarWrapper>;
  } else if (isTrackIn) {
    // Filter menu items based on the user permissions
    menuItems = getMainItems(currentConference, currentTrack, currentRole).filter((value) => {
      const route = getRouteByName(value.route_name);
      return route.permission ? can(route.permission) : true;
    });

    // Filter menu items when there are no keywords
    if (!trackUsingKeywords) {
      menuItems = menuItems.filter((menuItem) => menuItem.route_name !== 'RouteKeywords');
    }

    let index = menuItems.findIndex((menuItem) => menuItem.name == 'Chair settings');
    if (index > 0) {
      const subItems = menuItems[index].subitems ?? [];
      index = subItems.findIndex((menuItem) => menuItem.name == 'Keywords');
      if (index > 0) {
        subItems[index].warning = trackUsingKeywords == null; // Keywords pending to define
      }
    }

    // Filter menu items when there are not reviewer roles handling reviewers
    if (!existRolesHandleReviewers) {
      menuItems = menuItems.map((menuItem) => {
        return {
          ...menuItem,
          subitems: menuItem.subitems?.filter(
            (menuItem) => menuItem.route_name !== 'RouteAssignmentsReviewersToReviewers',
          ),
        };
      });
    }

    // Filter menu items when reviewer does not handle other reviewers
    if (currentRole.type == 'reviewer' && !doesHandleReviewers(currentRole)) {
      menuItems = menuItems.filter((menuItem) => menuItem.route_name !== 'RoutePeople');
    }

    // Hide bulk email reviewers if reviewer handles people instead of papers,
    // or has no supervised roles (via form read visibility)
    if (currentRole.type == 'reviewer') {
      const shouldHideBulkEmail =
        doesHandleReviewers(currentRole) ||
        getSupervisedRoleIds(formStoreState.formById, formStoreState.visibilityByFormId, currentRole.id).length === 0;
      if (shouldHideBulkEmail) {
        menuItems = menuItems.map((menuItem) => ({
          ...menuItem,
          subitems: menuItem.subitems?.filter((subItem) => subItem.route_name !== 'RouteBulkEmailReviewersPage'),
        }));
      }
    }

    // Hide or relabel "Bulk email supervisors" based on reviewer's supervising roles
    if (currentRole.type == 'reviewer') {
      const supervisorRoleIds = getSupervisorRoleIds(conferenceState.roleById, currentRole);
      if (supervisorRoleIds.length === 0) {
        menuItems = menuItems.map((menuItem) => ({
          ...menuItem,
          subitems: menuItem.subitems?.filter((subItem) => subItem.route_name !== 'RouteBulkEmailSupervisorsPage'),
        }));
      } else {
        const supervisorRoles = supervisorRoleIds
          .map((id) => conferenceState.roleById[id])
          .filter((r): r is Role => !!r);
        const label =
          supervisorRoles.length === 1
            ? t('Email {{role}}', { role: getRoleName(supervisorRoles[0], t) })
            : t('Email supervisors');
        menuItems = menuItems.map((menuItem) => ({
          ...menuItem,
          subitems: menuItem.subitems?.map((subItem) =>
            subItem.route_name === 'RouteBulkEmailSupervisorsPage' ? { ...subItem, displayName: label } : subItem,
          ),
        }));
      }
    }

    // Dashboard is only available to chairs
    if (currentRole.type != 'chair') {
      menuItems = menuItems.filter((menuItem) => menuItem.route_name !== 'RouteDashboard');
    }

    const roles = getUserRoles(currentTrack.id, conferenceState.userRolesByTrack, conferenceState.roleById);
    roles.forEach((role) => {
      roleOptions.push({ id: role.id.toString(), label: getRoleName(role, t) });
    });

    // Define header
    header = (
      <HeaderBarWrapper>
        {isMobile ? (
          logoWrapper
        ) : (
          <div className="flex items-center text-base align-baseline flex-1 min-w-0 conferenceNameWrapper">
            <ConversationIcon />
            <StyledConferenceTitle>
              <span className="ml-4 font-bold conference-name">{currentConference.name}</span>
              <span className="ml-1.5 mr-1">|</span>
              <span className="track-name">{currentTrack.name}</span>
            </StyledConferenceTitle>

            {currentRole.type === 'chair' && (
              <ShareLinkButton
                conferenceSlug={currentConference.slug}
                trackSlug={currentTrack?.slug ?? ''}
                className="ml-4 mr-4 whitespace-nowrap"
              />
            )}
          </div>
        )}
      </HeaderBarWrapper>
    );
  }

  const uiprops = {
    main_items: menuItems,
    selectedOption,
    doClick,
    logo,
    header,
    menuOpen,
    userName: userName,
    isImpersonated,
    currentConference,
    leaveImpersonationAction,
    currentTrack,
    backgroundColor,
    allowMobile,
    menuMobileOpen,
    handleOnMenuToggle,
    isMobile,
    isChair: currentRole?.type === 'chair',
  };

  return (
    <ThemeProvider theme={theme}>
      <MainLayout {...uiprops}>{children}</MainLayout>
    </ThemeProvider>
  );
};

const mapStateToProps = (state: AppState) => ({
  menuOpen: selectSideMenuState(state).menu_open,
  currentTrack: selectCurrentTrack(state),
  currentRole: selectCurrentRole(state),
  currentConference: selectCurrentConference(state),
  user: selectUser(state),
  conferenceState: state.conference,
  isImpersonated: !!selectImpersonatedTrackId(state),
  role: selectCurrentRole(state),
  trackUsingKeywords: selectTrackUsingKeywords(state),
  existRolesHandleReviewers: selectExistRolesHandleReviewers(state),
  isRequiredInfoCompleted: selectIsRequiredInfoCompleted(state),
  formStoreState: selectFormState(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setMenuOpen: (menuOpen: boolean) => dispatch(sidemenuSlice.actions.SIDEMENU_SET_MENU_OPEN(menuOpen)),
  menuclick: (item: string) => dispatch(sidemenuSlice.actions.SIDEMENU_CLICK(item)),
  selectRoleAction: (trackId: number, roleId: number) =>
    dispatch(conferenceSlice.actions.SELECT_ROLE({ trackId, roleId, doRedirect: true })),
  leaveImpersonationAction: () => dispatch(authSlice.actions.LEAVE_IMPERSONATION()),
});

const StyledBeta = styled.div`
  font-weight: bold;
  background-color: ${(props) => props.theme.palette.primary.main};
  width: max-content;
  padding: 1px 20px;
  color: #fff;
  font-size: 0.6rem;
  position: absolute;
  top: -8px;
  left: -21px;
  opacity: 1;
  transform: rotate(310deg);
  box-shadow: rgba(0, 0, 0, 0.4) -2px 2px 4px 0px;
`;

const StyledProfileMenu = styled.div`
  position: absolute;
  top: 4.2rem;
  z-index: 1000;
  background-color: #fafafa;
  padding: 1.5rem 2rem;
  min-width: 14rem;
  right: -1rem;

  div > div {
    margin-top: 0.3rem;
    margin-bottom: 0.3rem;
  }
`;

const StyledMenuProfile = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const StyledConferenceTitle = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  margin-right: 0.5rem;

  @media (max-width: 1585px) {
    .conference-name {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 80ch;
    }
    .track-name {
      white-space: nowrap;
    }
  }
  @media (min-width: 1200px) and (max-width: 1516px) {
    .conference-name {
      max-width: 60ch;
    }
  }
  @media (min-width: 1025px) and (max-width: 1200px) {
    .conference-name {
      max-width: 40ch;
    }
  }
  @media (min-width: 800px) and (max-width: 1024px) {
    .conference-name {
      max-width: 30ch;
    }
  }
`;

export default connect(mapStateToProps, mapDispatchToProps)(LayoutCtrl);
